import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Package, X, Loader, ChevronRight, Map, List, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';

export default function RegionalMapPage() {
  const [products, setProducts] = useState([]);
  const [regionGroups, setRegionGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    loadGoogleMapsScript();
  }, []);

  useEffect(() => {
    if (mapLoaded && Object.keys(regionGroups).length > 0 && viewMode === 'map') {
      initializeMap();
    }
  }, [mapLoaded, regionGroups, viewMode]);

  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setMapLoaded(true));
      if (window.google && window.google.maps) {
        setMapLoaded(true);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDuYoa4UDDKfJJUoqSikRvm2Sfln4LuVVk&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => toast.error('Failed to load Google Maps');
    document.head.appendChild(script);
  };

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Close any open info window
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    // Initialize map centered on India
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 20.5937, lng: 78.9629 },
      zoom: 5,
      minZoom: 4,
      maxZoom: 15,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        position: window.google.maps.ControlPosition.TOP_RIGHT
      },
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_CENTER
      },
      styles: [
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [{ color: '#e9e9e9' }, { lightness: 17 }]
        },
        {
          featureType: 'landscape',
          elementType: 'geometry',
          stylers: [{ color: '#f5f5f5' }, { lightness: 20 }]
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.fill',
          stylers: [{ color: '#ffffff' }, { lightness: 17 }]
        },
        {
          featureType: 'administrative.country',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#16a34a' }, { weight: 2 }]
        },
        {
          featureType: 'administrative.province',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#22c55e' }, { weight: 1 }]
        },
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    googleMapRef.current = map;

    // Create single info window instance
    infoWindowRef.current = new window.google.maps.InfoWindow();

    // Add markers for each region
    const geocoder = new window.google.maps.Geocoder();
    const bounds = new window.google.maps.LatLngBounds();

    Object.values(regionGroups).forEach((group, index) => {
      // Add delay to avoid rate limiting
      setTimeout(() => {
        geocodeAndAddMarker(map, group, geocoder, bounds);
      }, index * 200);
    });

  }, [regionGroups]);

  const geocodeAndAddMarker = (map, group, geocoder, bounds) => {
    const searchQuery = `${group.region}, India`;
    
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        bounds.extend(location);

        // Create custom marker
        const markerSize = Math.min(50, 30 + group.products.length * 3);
        
        const marker = new window.google.maps.Marker({
          map: map,
          position: location,
          title: group.region,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: markerSize / 4,
            fillColor: '#16a34a',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          label: {
            text: String(group.products.length),
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          animation: window.google.maps.Animation.DROP,
        });

        // Info window content
        const infoContent = `
          <div style="padding: 12px; min-width: 220px; font-family: system-ui, -apple-system, sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #16a34a, #22c55e); border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <div>
                <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #1f2937;">${group.region}</h3>
                <p style="margin: 4px 0 0 0; font-size: 13px; color: #6b7280;">${group.products.length} product${group.products.length !== 1 ? 's' : ''} available</p>
              </div>
            </div>
            ${group.description ? `<p style="margin: 0 0 12px 0; font-size: 13px; color: #4b5563; line-height: 1.4;">${group.description}</p>` : ''}
            <div style="display: flex; gap: 8px;">
              <button 
                onclick="window.dispatchEvent(new CustomEvent('viewRegionProducts', { detail: '${group.region}' }))"
                style="flex: 1; background: linear-gradient(135deg, #16a34a, #22c55e); color: white; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s;"
                onmouseover="this.style.transform='scale(1.02)'"
                onmouseout="this.style.transform='scale(1)'"
              >
                View Products
              </button>
            </div>
          </div>
        `;

        marker.addListener('click', () => {
          infoWindowRef.current.setContent(infoContent);
          infoWindowRef.current.open(map, marker);
        });

        // Hover effect
        marker.addListener('mouseover', () => {
          marker.setIcon({
            ...marker.getIcon(),
            fillColor: '#15803d',
            scale: (markerSize / 4) * 1.2,
          });
        });

        marker.addListener('mouseout', () => {
          marker.setIcon({
            ...marker.getIcon(),
            fillColor: '#16a34a',
            scale: markerSize / 4,
          });
        });

        markersRef.current.push(marker);

        // Fit map to bounds after all markers are added
        if (markersRef.current.length === Object.keys(regionGroups).length) {
          map.fitBounds(bounds, { padding: 50 });
        }
      }
    });
  };

  // Listen for view products event from info window
  useEffect(() => {
    const handleViewProducts = (event) => {
      setSelectedRegion(event.detail);
    };

    window.addEventListener('viewRegionProducts', handleViewProducts);
    return () => window.removeEventListener('viewRegionProducts', handleViewProducts);
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`);
      const allProducts = response.data;
      
      // Group products by region_name
      const grouped = {};
      allProducts.forEach(product => {
        if (product.region_name) {
          if (!grouped[product.region_name]) {
            grouped[product.region_name] = {
              region: product.region_name,
              description: product.region_description || '',
              products: []
            };
          }
          grouped[product.region_name].products.push(product);
        }
      });
      
      setProducts(allProducts);
      setRegionGroups(grouped);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load regional products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  const filteredRegions = Object.values(regionGroups).filter(group =>
    group.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const regionCount = Object.keys(regionGroups).length;
  const totalRegionalProducts = Object.values(regionGroups).reduce((sum, g) => sum + g.products.length, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="text-center">
          <Loader className="w-16 h-16 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading regional map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 via-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Regional Product Map</h1>
                <p className="text-green-100 mt-1">Discover authentic products from across India</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Stats */}
              <div className="flex items-center gap-6 bg-white/10 backdrop-blur rounded-xl px-6 py-3">
                <div className="text-center">
                  <div className="text-2xl font-bold">{regionCount}</div>
                  <div className="text-xs text-green-100">Regions</div>
                </div>
                <div className="h-8 w-px bg-white/30"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalRegionalProducts}</div>
                  <div className="text-xs text-green-100">Products</div>
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex bg-white/10 backdrop-blur rounded-lg p-1">
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'map' 
                      ? 'bg-white text-green-700' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid="map-view-btn"
                >
                  <Map className="w-4 h-4" />
                  Map
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white text-green-700' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                  List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {regionCount === 0 ? (
        <div className="flex items-center justify-center py-32">
          <Card className="p-12 text-center max-w-md">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Regional Products Yet</h3>
            <p className="text-gray-600">
              Products with regional information will appear here automatically
            </p>
          </Card>
        </div>
      ) : (
        <div className="flex h-[calc(100vh-140px)]">
          {/* Sidebar - Region List */}
          <div className={`${viewMode === 'list' ? 'w-full' : 'w-80'} bg-white border-r border-gray-200 flex flex-col`}>
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search regions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="region-search-input"
                />
              </div>
            </div>

            {/* Region Cards */}
            <div className={`flex-1 overflow-y-auto p-4 ${viewMode === 'list' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}`}>
              {filteredRegions.map((group) => (
                <button
                  key={group.region}
                  onClick={() => setSelectedRegion(selectedRegion === group.region ? null : group.region)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    selectedRegion === group.region
                      ? 'border-green-500 bg-green-50 shadow-md'
                      : 'border-gray-100 bg-white hover:border-green-200 hover:shadow-sm'
                  }`}
                  data-testid={`region-card-${group.region}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedRegion === group.region
                        ? 'bg-green-600'
                        : 'bg-gradient-to-br from-green-500 to-emerald-600'
                    }`}>
                      <span className="text-white font-bold text-lg">{group.products.length}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{group.region}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {group.products.length} product{group.products.length !== 1 ? 's' : ''}
                      </p>
                      {group.description && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{group.description}</p>
                      )}
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                      selectedRegion === group.region ? 'rotate-90 text-green-600' : ''
                    }`} />
                  </div>

                  {/* Product preview - show in list view */}
                  {viewMode === 'list' && (
                    <div className="mt-3 flex -space-x-2">
                      {group.products.slice(0, 4).map((product, idx) => (
                        <div
                          key={product._id || product.id}
                          className="w-10 h-10 rounded-lg border-2 border-white overflow-hidden"
                          style={{ zIndex: 4 - idx }}
                        >
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <Package className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                      ))}
                      {group.products.length > 4 && (
                        <div className="w-10 h-10 rounded-lg border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                          +{group.products.length - 4}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}

              {filteredRegions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No regions found matching "{searchQuery}"
                </div>
              )}
            </div>
          </div>

          {/* Map View */}
          {viewMode === 'map' && (
            <div className="flex-1 relative">
              {/* Google Map Container */}
              <div 
                ref={mapRef} 
                className="w-full h-full"
                data-testid="google-map-container"
              />

              {/* Map Loading State */}
              {!mapLoaded && (
                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <Loader className="w-10 h-10 text-green-600 animate-spin mx-auto mb-3" />
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}

              {/* Map Legend */}
              <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Map Legend</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">N</span>
                    </div>
                    <span className="text-sm text-gray-600">Product Region</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-green-600 bg-green-50"></div>
                    <span className="text-sm text-gray-600">Click for details</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selected Region Products Panel */}
          {selectedRegion && regionGroups[selectedRegion] && (
            <div className="w-96 bg-white border-l border-gray-200 flex flex-col shadow-xl">
              {/* Panel Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{selectedRegion}</h3>
                      <p className="text-green-100 text-sm">
                        {regionGroups[selectedRegion].products.length} products
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedRegion(null)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    data-testid="close-region-panel"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {regionGroups[selectedRegion].description && (
                  <p className="text-green-50 text-sm mt-3 leading-relaxed">
                    {regionGroups[selectedRegion].description}
                  </p>
                )}
              </div>

              {/* Products List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {regionGroups[selectedRegion].products.map(product => (
                  <button
                    key={product._id || product.id}
                    onClick={() => handleProductClick(product._id || product.id)}
                    className="w-full bg-white border border-gray-200 hover:border-green-300 hover:shadow-md rounded-xl p-4 transition-all text-left group"
                    data-testid={`product-card-${product._id || product.id}`}
                  >
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors line-clamp-2">
                          {product.name}
                        </h4>
                        <div className="flex items-baseline gap-2 mt-2">
                          <span className="text-lg font-bold text-green-600">
                            ₹{(product.sale_price || product.price).toLocaleString()}
                          </span>
                          {product.sale_price && (
                            <span className="text-sm text-gray-400 line-through">
                              ₹{product.price.toLocaleString()}
                            </span>
                          )}
                        </div>
                        {product.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {product.tags.slice(0, 2).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 flex-shrink-0 self-center" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
